import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Filter, Lock, Plus, Search, X, Info } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import { FilterChip, DraftFilterChip } from './FilterChip';
import { FilterWizardPeekPopover } from './FilterWizardPeekPopover';
import type {
  FilterCondition,
  FilterGroup,
  TableExplorerColumn,
} from '../../../core/types/workspaceTypes';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { normalizeColumnName } from '../../utils/normalizeColumnName';
import { findColumnByName } from '../../utils/columnNameMatch';
import {
  defaultDraftConditionFields,
  scanFiltersForChip,
  type JsonOperatorContext,
} from '../../utils/filterUtils';
import { useMetaInfo } from '../../hooks/useMetaInfo';
import { usePgServerMajorVersion } from '../../hooks/usePgServerMajorVersion';
import { useSqliteSpatialAvailable } from '../../hooks/useSqliteSpatialAvailable';
import { useAnalytics } from '../../../../../core/analytics';
import type { TableExplorerCellValueCodec } from '../../../table-explorer/components/tableExplorerHostPorts';

type FilterWizardProps = {
  filters: FilterGroup[];
  /**
   * System-applied filters inherited from a non-root FK Breadcrumb node
   * (the inbound-FK constraint that brought the user to this view). These
   * chips render with a lock icon and cannot be edited or removed. Combined
   * with `filters` to form the effective SQL filter set.
   */
  lockedFilters?: ReadonlyArray<FilterGroup>;
  columns: TableExplorerColumn[];
  onFiltersChange: (filters: FilterGroup[]) => void;
  // Optional: column to start with as an empty draft (from toolbar Add Filter button)
  initialDraftColumn?: string | null;
  // Optional: a fully pre-filled draft (from "Add to filter" cell context menu).
  // Takes precedence over initialDraftColumn when both are set.
  initialDraftFilter?: FilterGroup | null;
  // Called when the initial draft has been handled (committed or cancelled)
  onDraftHandled?: () => void;
  // Optional controls rendered at the trailing edge of the header row
  // (e.g. row-limit input + reload button on the data-profile workspace tab).
  trailingControls?: React.ReactNode;
  // Required for the FK lookup operator's row picker; without these the picker
  // is silently disabled and only the regular operators are available on FK
  // columns (used by surfaces like data-compare that don't have a connection).
  connectionId?: string;
  database?: string;
  // Source schema/table — required for the loose-FK target picker UI shown
  // when a non-FK column is filtered with the lookup operator. Without these,
  // FilterChip falls back to the legacy real-FK-only behavior.
  tableSchema?: string;
  tableName?: string;
  // Engine — required to enable the JSON filter operator's per-engine SQL
  // emit and the declare-on-text confirmation flow. When omitted, JSON
  // operator placement is skipped and the chip behaves as before.
  engine?: DatabaseEngineId;
  // Fires whenever the wizard's local draft state flips between dirty/clean.
  // Callers (e.g. TableExplorerTab) use this to gate other "Add Filter"
  // entry points outside the wizard.
  onDraftStateChange?: (hasDraft: boolean) => void;
  // Increment from outside to request the dirty draft chip pulse + tooltip.
  // Used by external "Add Filter" buttons (toolbar pickers) that should also
  // honor the "confirm or cancel first" rule.
  pulseRequestToken?: number;
  // Currently loaded grid rows — forwarded to (Draft)FilterChip so the
  // Declare-as-JSON confirmation can preview a real sample value from the
  // column. Optional; preview gracefully degrades when omitted.
  sampleRows?: ReadonlyArray<Record<string, unknown>>;
  valueCodec?: TableExplorerCellValueCodec;
  /**
   * Host value codecs only (Azure Table): the EDM tag of ONE operand of a chip condition (S4 review D, D3) —
   * `condition` undefined = a new condition, whose tag falls back to what the host observed for the field.
   */
  valueTagFor?: (field: string, condition: FilterCondition | undefined, operand: 'value' | 'value2') => string | undefined;
};

export function FilterWizard({
  filters,
  lockedFilters,
  columns,
  onFiltersChange,
  initialDraftColumn,
  initialDraftFilter,
  onDraftHandled,
  trailingControls,
  connectionId,
  database,
  tableSchema,
  tableName,
  engine,
  onDraftStateChange,
  pulseRequestToken,
  sampleRows,
  valueCodec,
  valueTagFor,
}: FilterWizardProps) {
  const lockedList = useMemo<ReadonlyArray<FilterGroup>>(
    () => lockedFilters ?? [],
    [lockedFilters],
  );
  const metaInfoQuery = useMetaInfo(connectionId ?? null, database ?? null);
  const metaInfo = metaInfoQuery.data;
  const pgMajorVersion = usePgServerMajorVersion(connectionId, database, engine);
  // SQLite spatial filters need the loadable `mod_spatialite` extension; probe
  // once per connection (cached) so the chip only offers spatial on SpatiaLite
  // databases. Every other engine gates on the static capability.
  const sqliteSpatialAvailable = useSqliteSpatialAvailable(engine, connectionId, database);
  const jsonContext: JsonOperatorContext | undefined = useMemo(() => {
    // Schemaless engines (Kusto/ADX) pass `schema: ''` — a valid, present
    // schema, not a missing one. Guard on `== null` so an empty-string schema
    // still builds the context; a falsy `!tableSchema` check would drop it and
    // suppress the `enum`/`json` operators for every Kusto column.
    if (!engine || tableSchema == null || !tableName) return undefined;
    return { engine, schema: tableSchema, table: tableName, metaInfo, pgMajorVersion, sqliteSpatialAvailable };
  }, [engine, metaInfo, pgMajorVersion, tableName, tableSchema, sqliteSpatialAvailable]);
  const { track } = useAnalytics();
  const [isAddingFilter, setIsAddingFilter] = useState(false);
  const [columnSearch, setColumnSearch] = useState('');
  // Draft filter being edited (not yet committed)
  const [draftFilter, setDraftFilter] = useState<FilterGroup | null>(null);
  // Per-committed-chip uncommitted edits (inline value edits, JSON-operator
  // staged edits, OR-clause drafts). Used solely by the *Peek matching rows*
  // popover so the preview reflects work-in-progress, not just Applied state.
  // Keyed by the chip's filter index.
  const [dirtyByIndex, setDirtyByIndex] = useState<Record<number, FilterGroup>>({});
  const handleChipDirtyChange = useCallback((index: number, dirty: FilterGroup | null) => {
    setDirtyByIndex((prev) => {
      if (dirty == null) {
        if (!(index in prev)) return prev;
        const next = { ...prev };
        delete next[index];
        return next;
      }
      return { ...prev, [index]: dirty };
    });
  }, []);
  // Currently selected column index for keyboard navigation
  const [selectedColumnIndex, setSelectedColumnIndex] = useState(0);
  // Increments each time the user tries to open Add Filter while a draft is
  // still pending — drives the pulse + tooltip on the dirty draft chip.
  const [draftPulseToken, setDraftPulseToken] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  // Track if we've already processed the initial draft column
  const processedInitialColumnRef = useRef<string | null>(null);
  // Track last seen pulseRequestToken to dedupe external pulse requests.
  const lastPulseRequestRef = useRef<number | undefined>(pulseRequestToken);

  // Report draft-state transitions so external "Add Filter" entry points
  // (e.g. the toolbar's AddColumnButton) can gate their popovers.
  const hasDraft = draftFilter !== null;
  const onDraftStateChangeRef = useRef(onDraftStateChange);
  onDraftStateChangeRef.current = onDraftStateChange;
  useEffect(() => {
    onDraftStateChangeRef.current?.(hasDraft);
  }, [hasDraft]);
  // On unmount, signal "no draft" — otherwise a parent that conditionally
  // renders the wizard (e.g. TableExplorerTab gates on
  // filters.length > 0 || pendingFilterColumn || pendingDraftFilter) is left
  // with a stale `hasDraft=true` after the user cancels the only draft and
  // the wrapping conditional drops the wizard.
  useEffect(() => {
    return () => onDraftStateChangeRef.current?.(false);
  }, []);

  // Bump the internal pulse token when an external entry point asks us to.
  useEffect(() => {
    if (pulseRequestToken === undefined) return;
    if (pulseRequestToken === lastPulseRequestRef.current) return;
    lastPulseRequestRef.current = pulseRequestToken;
    if (draftFilter !== null) {
      setDraftPulseToken((t) => t + 1);
    }
  }, [pulseRequestToken, draftFilter]);

  // Count of committed filters per column (case-insensitive). Used to render a
  // small "(N)" hint next to columns in the picker so the user knows whether
  // they're adding the first filter on a column or stacking another one.
  // Locked filters are counted too — the user already has a filter on that
  // column even if they can't edit it.
  const filterCountByField = useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of [...lockedList, ...filters]) {
      const key = normalizeColumnName(f.field);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [filters, lockedList]);

  // Filter columns by search
  const filteredColumns = useMemo(() => {
    if (!columnSearch.trim()) return columns;
    const search = columnSearch.toLowerCase();
    return columns.filter((col) => col.name.toLowerCase().includes(search));
  }, [columns, columnSearch]);

  // Every column is selectable — multiple filters per column are allowed and
  // get AND-ed together by `buildFilterWhereClause`.
  const selectableColumns = filteredColumns;

  // Reset selected index when search changes or popover opens
  useEffect(() => {
    setSelectedColumnIndex(0);
  }, [columnSearch, isAddingFilter]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedColumnIndex >= 0) {
      const selectedButton = listRef.current.querySelector(`[data-index="${selectedColumnIndex}"]`);
      selectedButton?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedColumnIndex]);

  // Create draft from initialDraftFilter (pre-filled, e.g. cell context menu)
  // or initialDraftColumn (empty, e.g. toolbar Add Filter picker). When a
  // dirty draft is already in progress, every entry point pulses the existing
  // chip instead of overwriting / silently dropping the request — the user
  // must confirm or cancel before adding another.
  useEffect(() => {
    if (initialDraftFilter) {
      const normalizedField = normalizeColumnName(initialDraftFilter.field);
      if (normalizedField === processedInitialColumnRef.current) return;

      if (draftFilter) {
        // Pulse and mark this request as seen. Don't call onDraftHandled —
        // clearing the parent's pending state can unmount the wizard (gated
        // on it) and lose the local dirty draft. Parent state is cleared
        // when the user eventually commits or cancels the dirty chip.
        setDraftPulseToken((t) => t + 1);
        processedInitialColumnRef.current = normalizedField;
        return;
      }

      setDraftFilter(initialDraftFilter);
      processedInitialColumnRef.current = normalizedField;
      return;
    }

    const normalizedInitialDraftColumn = initialDraftColumn
      ? normalizeColumnName(initialDraftColumn)
      : null;

    if (!normalizedInitialDraftColumn) return;
    if (normalizedInitialDraftColumn === processedInitialColumnRef.current) return;

    if (draftFilter) {
      setDraftPulseToken((t) => t + 1);
      processedInitialColumnRef.current = normalizedInitialDraftColumn;
      return;
    }

    const column = findColumnByName(columns, initialDraftColumn!);
    if (column) {
      const newDraft: FilterGroup = {
        field: column.name,
        conditions: [defaultDraftConditionFields(column, jsonContext)],
      };
      setDraftFilter(newDraft);
      processedInitialColumnRef.current = normalizedInitialDraftColumn;
    }
  }, [initialDraftColumn, initialDraftFilter, columns, draftFilter, jsonContext]);

  // Get column by name — the chip's column (its FK lookup target, type, …).
  const getColumn = useCallback((fieldName: string) => findColumnByName(columns, fieldName), [columns]);

  // Start adding a new filter for a column (creates draft)
  const handleStartAddFilter = useCallback(
    (column: TableExplorerColumn) => {
      const newDraft: FilterGroup = {
        field: column.name,
        conditions: [defaultDraftConditionFields(column, jsonContext)],
      };

      setDraftFilter(newDraft);
      setIsAddingFilter(false);
      setColumnSearch('');
    },
    [jsonContext]
  );

  // Commit the draft filter to the actual filters
  const handleCommitDraft = useCallback((draftOverride?: FilterGroup) => {
    const draftToCommit = draftOverride ?? draftFilter;
    if (draftToCommit) {
      // Fire `feature:json_filter_apply` for each `json`-operator condition
      // in the committed draft. Low-cardinality props only — no path strings,
      // no values.
      if (engine) {
        for (const condition of draftToCommit.conditions) {
          if (condition.operator === 'json') {
            track('feature:json_filter_apply', {
              engine,
              sub_operator: condition.jsonSubOperator ?? 'has_property',
              has_path: !!condition.path,
            });
          }
        }
      }
      onFiltersChange([...filters, draftToCommit]);
      setDraftFilter(null);
      processedInitialColumnRef.current = null;
      onDraftHandled?.();
    }
  }, [draftFilter, engine, filters, onDraftHandled, onFiltersChange, track]);

  // Cancel the draft filter
  const handleCancelDraft = useCallback(() => {
    setDraftFilter(null);
    processedInitialColumnRef.current = null;
    onDraftHandled?.();
  }, [onDraftHandled]);

  // Update the draft filter
  const handleUpdateDraft = useCallback((updatedDraft: FilterGroup) => {
    setDraftFilter(updatedDraft);
  }, []);

  // Update a committed filter group
  const handleUpdateFilter = useCallback(
    (index: number, updatedGroup: FilterGroup) => {
      const newFilters = [...filters];
      newFilters[index] = updatedGroup;
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  // Remove a committed filter group
  const handleRemoveFilter = useCallback(
    (index: number) => {
      const newFilters = filters.filter((_, i) => i !== index);
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  // Clear all filters
  const handleClearAll = useCallback(() => {
    onFiltersChange([]);
    setDraftFilter(null);
  }, [onFiltersChange]);

  // Handle keyboard navigation in the column list
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (selectableColumns.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedColumnIndex((prev) =>
            prev < selectableColumns.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedColumnIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedColumnIndex >= 0 && selectedColumnIndex < selectableColumns.length) {
            handleStartAddFilter(selectableColumns[selectedColumnIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsAddingFilter(false);
          break;
      }
    },
    [selectableColumns, selectedColumnIndex, handleStartAddFilter]
  );

  const totalFilterCount = filters.length + (draftFilter ? 1 : 0);

  return (
    // PP-11 — the filter row gets its own surface token (`bg-chrome`,
    // `border-divider`) so it reads as chrome that frames the grid rather
    // than a plain data row on `bg-background`. Matches MainToolbar's
    // titlebar treatment (same token pairing).
    <div className="flex flex-col gap-1 px-2 py-1 border-b border-divider bg-chrome">
      {/* Header row. `select-none` stops here on purpose: the chips row below
          carries column names and user-entered filter values, which stay
          selectable per CH-25's "names and values" rule. */}
      <div className="flex items-center gap-2 select-none">
        <div
          className="flex items-center gap-1 text-xs text-muted-foreground"
          data-testid="filter-wizard-header"
        >
          <Filter className="h-3.5 w-3.5" />
          <span>Filters</span>
          {(filters.length + lockedList.length) > 0 && (
            <span
              className="text-foreground font-medium"
              data-testid="filter-wizard-filter-count"
            >
              ({filters.length + lockedList.length})
            </span>
          )}
        </div>

        {/* Add filter button.
            When a draft is dirty we render a plain Button (no Radix Popover
            at all) so there's no onOpenToggle to suppress — clicking just
            pulses the chip + shows the "confirm or cancel first" tooltip.
            Earlier attempts to block via preventDefault on the trigger were
            unreliable in subsequent clicks, so this strips the Popover out
            entirely while a draft is pending. */}
        {draftFilter !== null ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            disabled={columns.length === 0}
            onClick={() => setDraftPulseToken((t) => t + 1)}
            data-testid="wizard-add-filter"
            data-blocked="true"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Filter
          </Button>
        ) : (
          <Popover open={isAddingFilter} onOpenChange={setIsAddingFilter}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                disabled={columns.length === 0}
                data-testid="wizard-add-filter"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Filter
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="start" portalled={false}>
              <div className="flex flex-col gap-2">
                <div className="text-xs font-medium text-muted-foreground">
                  Select a column to filter
                </div>
                {/* Search input */}
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search columns..."
                    className="h-7 pl-7 text-xs"
                    autoFocus
                  />
                </div>
                {/* Column list */}
                <div className="max-h-48 overflow-y-auto" ref={listRef}>
                  {filteredColumns.length === 0 ? (
                    <div className="text-xs text-muted-foreground py-2 text-center">
                      No columns found
                    </div>
                  ) : (
                    filteredColumns.map((col, index) => {
                      const existingCount =
                        filterCountByField.get(normalizeColumnName(col.name)) ?? 0;
                      const isSelected = index === selectedColumnIndex;
                      return (
                        <button
                          key={col.name}
                          data-index={index}
                          onClick={() => handleStartAddFilter(col)}
                          onMouseEnter={() => setSelectedColumnIndex(index)}
                          className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded ${
                            isSelected ? 'bg-accent' : 'hover:bg-accent cursor-pointer'
                          }`}
                        >
                          <span className="font-medium flex items-center gap-1">
                            {col.name}
                            {existingCount > 0 && (
                              <span
                                className="text-[10px] text-muted-foreground"
                                data-testid={`column-picker-filter-count-${normalizeColumnName(col.name)}`}
                              >
                                ({existingCount})
                              </span>
                            )}
                          </span>
                          <span className="text-muted-foreground text-[10px]">
                            {col.dataType}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Hint about context menu */}
        <div
          data-testid="filter-wizard-context-menu-hint"
          className="flex items-center gap-1 text-[10px] text-muted-foreground/50"
        >
          <Info className="h-3 w-3" />
          <span>or right-click a column/cell to add</span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Peek matching rows — generic affordance for previewing the data
            that the current filters select (LIMIT 50). Effective filters:
            – each committed chip's staged inline edits (value edits,
              JSON-operator staged changes, draft OR-clause) override the
              committed snapshot via `dirtyByIndex`;
            – any new draft chip is appended.
            So the peek reflects work-in-progress, not just Applied state.
            Hidden when the wizard doesn't have a connection + table context
            (e.g. inside data-compare, which configures filters without a
            live table). */}
        <FilterWizardPeekPopover
          connectionId={connectionId}
          database={database}
          tableSchema={tableSchema}
          tableName={tableName}
          engine={engine}
          filters={(() => {
            const effective = filters.map((f, i) => dirtyByIndex[i] ?? f);
            const withDraft = draftFilter ? [...effective, draftFilter] : effective;
            return [...lockedList, ...withDraft];
          })()}
        />

        {/* Clear all button */}
        {totalFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
          >
            <X className="h-3 w-3 mr-1" />
            Clear All
          </Button>
        )}

        {trailingControls}
      </div>

      {/*
        Filter chips. Note: do NOT add Tailwind's `@container` to this row.
        `container-type: inline-size` applies layout containment, which
        floating-ui treats as a containing block for `position: fixed`.
        FilterChip's operator <Select> renders inline (portalled={false})
        per the portable-host design — under @container the popper-wrapper
        lands at viewport top-left instead of below its trigger.
      */}
      {(filters.length > 0 || draftFilter || lockedList.length > 0) && (
        <div className="flex flex-wrap items-center gap-1">
          {/* Locked (inbound-FK) filters — rendered first as immutable chips
              with a lock icon. Inherited from a non-root FK breadcrumb node;
              the user cannot edit or remove them here. */}
          {lockedList.map((filterGroup, index) => (
            <div
              key={`locked-${filterGroup.field}-${index}`}
              className="flex items-center gap-1"
            >
              {index > 0 && (
                <span className="text-xs text-muted-foreground font-medium px-1">
                  AND
                </span>
              )}
              <LockedFilterChip filterGroup={filterGroup} />
            </div>
          ))}

          {/* Committed filters */}
          {filters.map((filterGroup, index) => (
            <div key={`${filterGroup.field}-${index}`} className="flex items-center gap-1">
              {(index > 0 || lockedList.length > 0) && (
                <span className="text-xs text-muted-foreground font-medium px-1">
                  AND
                </span>
              )}
              <FilterChip
                filterGroup={filterGroup}
                column={getColumn(filterGroup.field)}
                onUpdate={(updated) => handleUpdateFilter(index, updated)}
                onRemove={() => handleRemoveFilter(index)}
                connectionId={connectionId}
                database={database}
                tableSchema={tableSchema}
                tableName={tableName}
                jsonContext={jsonContext}
                sampleRows={sampleRows}
                onDirtyChange={(d) => handleChipDirtyChange(index, d)}
                scanFilters={scanFiltersForChip(lockedList, filters, index)}
                valueCodec={valueCodec}
                valueTagFor={valueTagFor ? (condition, operand) => valueTagFor(filterGroup.field, condition, operand) : undefined}
              />
            </div>
          ))}

          {/* Draft filter */}
          {draftFilter && (
            <div className="flex items-center gap-1">
              {(filters.length > 0 || lockedList.length > 0) && (
                <span className="text-xs text-muted-foreground font-medium px-1">
                  AND
                </span>
              )}
              <DraftFilterChip
                filterGroup={draftFilter}
                column={getColumn(draftFilter.field)}
                onUpdate={handleUpdateDraft}
                onCommit={handleCommitDraft}
                onCancel={handleCancelDraft}
                connectionId={connectionId}
                database={database}
                tableSchema={tableSchema}
                tableName={tableName}
                pulseToken={draftPulseToken}
                jsonContext={jsonContext}
                sampleRows={sampleRows}
                scanFilters={scanFiltersForChip(lockedList, filters, null)}
                valueCodec={valueCodec}
                valueTagFor={valueTagFor ? (condition, operand) => valueTagFor(draftFilter.field, condition, operand) : undefined}
              />
            </div>
          )}
        </div>
      )}

    </div>
  );
}

/**
 * Compact, immutable presenter for a system-applied filter — used to render
 * the inbound-FK constraint inherited from a non-root FK Breadcrumb node.
 * No popovers, no remove button: the chip is purely informational and the
 * underlying SQL is fixed.
 */
function LockedFilterChip({ filterGroup }: { filterGroup: FilterGroup }) {
  const summary = describeFilterCondition(filterGroup);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-[11px] text-amber-700 dark:text-amber-300"
      data-testid="filter-wizard-locked-chip"
      data-field={filterGroup.field}
      title={`${filterGroup.field}: ${summary} — locked by breadcrumb context`}
    >
      <Lock className="h-3 w-3" aria-hidden="true" />
      <span className="font-medium">{filterGroup.field}</span>
      <span className="text-amber-700/80 dark:text-amber-300/80">{summary}</span>
    </span>
  );
}

function describeFilterCondition(filterGroup: FilterGroup): string {
  const first = filterGroup.conditions[0];
  if (!first) return '';
  const op = first.operator ?? '=';
  if (first.value === undefined || first.value === null) return op;
  return `${op} ${String(first.value)}`;
}
